import {
  BarcodeInspectionType,
  type InternalLabelArea,
  LabelInspectionAreaType,
} from '@ulysses-inc/harami_api_client'
import { clampRect } from '../../normalizedRect'
import type { PreservedAreaSettings } from '../usePreservedAreaSettings'
import { AREA_FORM_DEFAULT_VALUES, AreaFormValues } from './useAreaForm'

/** InternalLabelArea → AreaFormValues */
export const toFormValues = ({
  area,
  preservedSettings,
}: {
  area?: InternalLabelArea
  preservedSettings?: PreservedAreaSettings
}): AreaFormValues => {
  // 新規作成の場合は、初期値で返す
  if (!area) {
    return {
      ...AREA_FORM_DEFAULT_VALUES,
      ...(preservedSettings && {
        expirationYear: preservedSettings.expirationYear,
        expirationMonth: preservedSettings.expirationMonth,
        expirationDay: preservedSettings.expirationDay,
        expirationHour: preservedSettings.expirationHour,
        expirationNotationOrder: preservedSettings.expirationNotationOrder,
      }),
      id: crypto.randomUUID(),
    }
  }

  const base: Pick<
    AreaFormValues,
    'id' | 'name' | 'x' | 'y' | 'width' | 'height'
  > = {
    id: area.id,
    name: area.name,
    x: area.x,
    y: area.y,
    width: area.width,
    height: area.height,
  }

  switch (area.type) {
    case LabelInspectionAreaType.PATTERN:
      return { ...AREA_FORM_DEFAULT_VALUES, ...base, type: 'pattern' }
    case LabelInspectionAreaType.ALPHANUMERIC:
      return {
        ...AREA_FORM_DEFAULT_VALUES,
        ...base,
        type: 'alphanumeric',
        alphanumericValue: area.value ?? '',
      }
    case LabelInspectionAreaType.BARCODE:
      return {
        ...AREA_FORM_DEFAULT_VALUES,
        ...base,
        type: 'barcode',
        barcodeValue: area.value ?? '',
        barcodeInspectionType:
          area.barcodeInspectionType ?? BarcodeInspectionType.MATCH,
      }
    case LabelInspectionAreaType.ALPHANUMERIC_INPUT:
      return {
        ...AREA_FORM_DEFAULT_VALUES,
        ...base,
        type: 'alphanumericInput',
      }
    case LabelInspectionAreaType.EXPIRATION:
      // TODO: OpenAPIに定義した型が間違ってoptionalになりまくっているので、正しくrequiredを定義した後、こちらのオプショナルチェーンを外す
      return {
        ...AREA_FORM_DEFAULT_VALUES,
        ...base,
        type: 'expiration',
        expirationLogic:
          area.expirationLogic ?? AREA_FORM_DEFAULT_VALUES.expirationLogic,
        expirationYear: area.year ?? AREA_FORM_DEFAULT_VALUES.expirationYear,
        expirationMonth: area.month ?? AREA_FORM_DEFAULT_VALUES.expirationMonth,
        expirationDay: area.day ?? AREA_FORM_DEFAULT_VALUES.expirationDay,
        expirationHour: area.hour ?? AREA_FORM_DEFAULT_VALUES.expirationHour,
        expirationNotationOrder:
          area.notationOrder ??
          AREA_FORM_DEFAULT_VALUES.expirationNotationOrder,
        expirationDuration: {
          years:
            area.yearsUntilExpiration ??
            AREA_FORM_DEFAULT_VALUES.expirationDuration.years,
          months:
            area.monthsUntilExpiration ??
            AREA_FORM_DEFAULT_VALUES.expirationDuration.months,
          days:
            area.daysUntilExpiration ??
            AREA_FORM_DEFAULT_VALUES.expirationDuration.days,
          hours:
            area.hoursUntilExpiration ??
            AREA_FORM_DEFAULT_VALUES.expirationDuration.hours,
        },
      }
    default:
      return { ...AREA_FORM_DEFAULT_VALUES, ...base, type: 'pattern' }
  }
}

/** AreaFormValues → InternalLabelArea */
export const toArea = (formValues: AreaFormValues): InternalLabelArea => {
  const base = clampRect({
    id: formValues.id,
    name: formValues.name,
    width: formValues.width,
    height: formValues.height,
    x: formValues.x,
    y: formValues.y,
  })

  switch (formValues.type) {
    case 'expiration':
      return {
        ...base,
        type: LabelInspectionAreaType.EXPIRATION,
        year: formValues.expirationYear,
        month: formValues.expirationMonth,
        day: formValues.expirationDay,
        hour: formValues.expirationHour,
        notationOrder: formValues.expirationNotationOrder,
        expirationLogic: formValues.expirationLogic,
        yearsUntilExpiration: formValues.expirationDuration.years,
        monthsUntilExpiration: formValues.expirationDuration.months,
        daysUntilExpiration: formValues.expirationDuration.days,
        hoursUntilExpiration: formValues.expirationDuration.hours,
      }
    case 'alphanumeric':
      return {
        ...base,
        type: LabelInspectionAreaType.ALPHANUMERIC,
        value: formValues.alphanumericValue,
      }
    case 'barcode':
      return {
        ...base,
        type: LabelInspectionAreaType.BARCODE,
        barcodeInspectionType: formValues.barcodeInspectionType,
        // read の場合は value を送らない（期待値不要）
        ...(formValues.barcodeInspectionType ===
          BarcodeInspectionType.MATCH && {
          value: formValues.barcodeValue,
        }),
      }
    case 'alphanumericInput':
      return {
        ...base,
        type: LabelInspectionAreaType.ALPHANUMERIC_INPUT,
      }
    default:
      return {
        ...base,
        type: LabelInspectionAreaType.PATTERN,
      }
  }
}
